import React, { useEffect, useState } from "react";
import { fetchSheetRows } from "./utils/fetchSheetRows";
import { CHANGE_STATUS, formatDate, formatDateWithTime, SHEET_URL } from "./constants";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import Select, { components } from "react-select";

// Add Poppins font
if (!document.getElementById("poppins-font")) {
  const poppinsFont = document.createElement("link");
  poppinsFont.id = "poppins-font";
  poppinsFont.rel = "stylesheet";
  poppinsFont.href = "https://fonts.googleapis.com/css?family=Poppins:400,500,600,700&display=swap";
  document.head.appendChild(poppinsFont);
}

// Add this function above your Dashboard component or import it from your API utils
async function updateUserStatusInSheet(row, newStatus) {
  const params = new URLSearchParams({
    name: row.name,
    type: row.type,
    dob: row.dob,
    schoolName: row.schoolName,
    status: newStatus
  });
  const url = `${CHANGE_STATUS}?${params.toString()}`;
  try {
    const response = await fetch(url, { method: "GET" });
    return await response.json();
  } catch (error) {
    console.error("Failed to update status in sheet:", error);
    return null;
  }
}

function SchoolOption(props) {
  return (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        tabIndex={-1}
        style={{ marginRight: 8, accentColor: "#263D75" }}
      />
      {props.label}
    </components.Option>
  );
}

function SchoolMenuList(props) {
  const { schoolOptions, selectedSchools } = props.selectProps;
  const allSelected = selectedSchools.length === schoolOptions.length && schoolOptions.length > 0;

  const handleSelectAll = () => {
    props.selectProps.onPendingSchoolChange(allSelected ? [] : schoolOptions);
  };

  return (
    <components.MenuList {...props}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom: "1px solid #e6ebf5",
          color: "#263D75",
          fontWeight: 600,
          cursor: "pointer"
        }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          onChange={handleSelectAll}
          onClick={event => event.stopPropagation()}
          style={{ accentColor: "#263D75" }}
        />
        Select All
      </label>
      {props.children}
    </components.MenuList>
  );
}

function SchoolValueContainer(props) {
  const selectedCount = props.selectProps.selectedSchools.length;
  return (
    <components.ValueContainer {...props}>
      <span style={{ color: "#263D75", fontWeight: 600 }}>
        {selectedCount === 1 ? "1 school selected" : `${selectedCount} schools selected`}
      </span>
      {props.children[1]}
    </components.ValueContainer>
  );
}

function Dashboard({ user, selectedSchoolName, onSchoolChange, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ trainers: 0, students: 0, systems: 0 });
  const [schoolRows, setSchoolRows] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [pendingLogout, setPendingLogout] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const isSuperAdmin = user.type?.trim().toLowerCase() === "super admin";
  const isAdmin = user.type?.trim().toLowerCase() === "admin";
  const selectedSchools = Array.isArray(selectedSchoolName) ? selectedSchoolName : [selectedSchoolName].filter(Boolean);
  const [pendingSchoolSelection, setPendingSchoolSelection] = useState(selectedSchools);

  const handlePendingSchoolChange = nextSchools => {
    setPendingSchoolSelection(nextSchools);
    if (nextSchools.length === 0) {
      onSchoolChange([]);
    }
  };

  useEffect(() => {
    setPendingSchoolSelection(selectedSchools);
  }, [selectedSchoolName]);

  // Sorting logic
  const sortedRows = React.useMemo(() => {
    if (!sortConfig.key) return schoolRows;
    const sorted = [...schoolRows].sort((a, b) => {
      let valA = a[sortConfig.key] ?? "";
      let valB = b[sortConfig.key] ?? "";
      // For numeric sort (volume)
      if (sortConfig.key === "volume") {
        valA = Number(valA);
        valB = Number(valB);
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [schoolRows, sortConfig]);

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      const rows = await fetchSheetRows(SHEET_URL);
      const schools = [...new Set(
        rows
          .map(row => String(row.schoolName || "").trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      setSchoolOptions(schools);
      const selectedSchools = Array.isArray(selectedSchoolName)
        ? selectedSchoolName
        : [selectedSchoolName];
      const schoolsForData = isSuperAdmin
        ? selectedSchools
        : [user.schoolName].filter(Boolean);
      // Filter: only rows with a non-empty name and matching schoolName
      const filtered = rows.filter(
        r =>
          schoolsForData.includes(r.schoolName) &&
          r.name && r.name.trim() !== "" &&
          r.type &&
          r.type.toLowerCase() !== "admin" &&
          r.type.toLowerCase() !== "super admin"
      );
      setSchoolRows(filtered);

      const trainers = filtered.filter(r => r.type?.toLowerCase() === "trainer").length;
      const students = filtered.filter(r => r.type?.toLowerCase() === "student").length;
      const systems = filtered.filter(r => r.type?.toLowerCase() === "system").length;

      // Calculate Total Licenses and Total Activated (only trainers, students, systems)
      const relevantUsers = filtered.filter(r =>
        r.type?.toLowerCase() === "trainer" ||
        r.type?.toLowerCase() === "student" ||
        r.type?.toLowerCase() === "system"
      );
      const totalLicenses = relevantUsers.length;
      const totalActivated = relevantUsers.filter(r => r.status && r.status.trim().toLowerCase() === "active").length;

      setStats({
        trainers,
        students,
        systems,
        totalLicenses,
        totalActivated
      });
      setLoading(false);
    }
    fetchStats();
  }, [isSuperAdmin, isAdmin, selectedSchoolName, user.schoolName]);

  // Warn on browser back/close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Are you sure you want to logout?";
      return "Are you sure you want to logout?";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (e) => {
      setShowLogoutModal(true);
      setPendingLogout(true);
      window.history.pushState(null, document.title, window.location.href);
    };
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Use bold, distinct colors
  const colorTrainers = "#FF5722";   // Deep Orange
  const colorStudents = "#009688";   // Teal
  const colorSystems = "#3F51B5";    // Indigo

  const chartOptions = {
    chart: { type: "column", backgroundColor: "#fff" },
    title: { text: "School Overview", style: { color: "#263D75" } },
    xAxis: {
      categories: ["Trainers", "Students", "Systems"],
      title: { text: null },
      labels: { style: { color: "#263D75", fontWeight: "bold" } }
    },
    yAxis: {
      min: 0,
      title: { text: "Count", style: { color: "#263D75" } },
      labels: { style: { color: "#263D75" } }
    },
    series: [
      {
        name: "Count",
        data: [
          { y: stats.trainers, color: colorTrainers },
          { y: stats.students, color: colorStudents },
          { y: stats.systems, color: colorSystems }
        ],
      }
    ],
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      series: {
        cursor: "pointer",
        point: {
          events: {
            click: function () {
              if (this.category === "Trainers") {
                navigate("/trainers");
              }
            }
          }
        }
      }
    }
  };

  const cardBaseStyle = {
    background: "#fff",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
    padding: "2rem 2.5rem", // Increased padding for more width
    minWidth: 200,          // Increased minWidth for wider cards
    textAlign: "center",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    margin: 0
  };



  // Handler to update status in the table (local state only)
  const handleStatusChange = async (idx, newStatus) => {
    const statusToSend = newStatus === "Pending" ? "" : newStatus; // Send blank if Pending
    setSchoolRows(prevRows =>
      prevRows.map((row, i) =>
        i === idx ? { ...row, status: newStatus } : row
      )
    );
    setLoading(true);
    const row = schoolRows[idx];
    await updateUserStatusInSheet(row, statusToSend);
    setLoading(false);
  };

  function exportTableAsPDF() {
    const doc = new jsPDF("p", "pt", "a4");
    let y = 30;

    // Title
    doc.setFontSize(20);
    doc.setTextColor("#263D75");
    doc.text("School Users Overview", 40, y);

    // Cards
    y += 30;
    doc.setFontSize(12);
    const cardData = [
      { label: "Total Trainers", value: stats.trainers, color: "#FF5722" },
      { label: "Total Students", value: stats.students, color: "#009688" },
      { label: "Total Systems", value: stats.systems, color: "#3F51B5" }
    ];
    let cardX = 40;
    cardData.forEach(card => {
      doc.setFillColor(card.color);
      doc.rect(cardX, y, 90, 50, "F");
      doc.setTextColor("#fff");
      doc.setFontSize(16);
      doc.text(String(card.value), cardX + 45, y + 22, { align: "center" });
      doc.setFontSize(9);
      doc.text(card.label, cardX + 45, y + 40, { align: "center" });
      cardX += 100;
    });

    // Graph (capture as image)
    const chartEl = document.querySelector(".dashboard-chart-pdf");
    if (chartEl) {
      html2canvas(chartEl, { backgroundColor: "#fff", scale: 2 }).then(canvas => {
        const imgData = canvas.toDataURL("image/png");
        y += 70;
        doc.addImage(imgData, "PNG", 40, y, 480, 220);
        y += 230;

        // Table
        const tableColumn = [
          "Name", "Type", "Standard", "Volume", "License_key", "Password", "Status", "Last Used on", "DOB"
        ];
        const tableRows = schoolRows.map(row => [
          row.name,
          row.type,
          row.standard,
          row.volume,
          row.license,
          row.password,
          row.status && row.status.trim()
            ? (row.status.trim().toLowerCase() === "inactive"
              ? "Deactivated"
              : row.status.trim().toLowerCase() === "pending" || row.status.trim() === ""
                ? "Not Activated"
                : row.status)
            : "Not Activated",
          row.last_used_on,
          row.dob
        ]);

        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: y,
          styles: { font: "poppins", fontSize: 9 }
        });

        doc.save("school_users.pdf");
      });
    } else {
      // fallback: just table
      y += 80;
      const tableColumn = [
        "Name", "Type", "Standard", "Volume", "License_key", "Password", "Status", "Last Used on", "DOB"
      ];
      const tableRows = schoolRows.map(row => [
        row.name,
        row.type,
        row.standard,
        row.volume,
        row.license,
        row.password,
        row.status && row.status.trim()
          ? (row.status.trim().toLowerCase() === "inactive"
            ? "Deactivated"
            : row.status.trim().toLowerCase() === "pending" || row.status.trim() === ""
              ? "Not Activated"
              : row.status)
          : "Not Activated",
        row.last_used_on,
        row.dob
      ]);
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: y,
        styles: { font: "poppins", fontSize: 9 }
      });
      doc.save("school_users.pdf");
    }
  }

  // Helper for card click
  const handleCardClick = (type) => {
    if (type === "trainers" && stats.trainers === 0) {
      setInfoMsg("No trainers found for this school.");
      setTimeout(() => setInfoMsg(""), 2500); // Hide after 2.5 seconds
      return;
    }
    if (type === "students" && stats.students === 0) {
      setInfoMsg("No students found for this school.");
      setTimeout(() => setInfoMsg(""), 2500);
      return;
    }
    if (type === "systems" && stats.systems === 0) {
      setInfoMsg("No systems found for this school.");
      setTimeout(() => setInfoMsg(""), 2500);
      return;
    }
    setInfoMsg("");
    navigate(`/${type}`);
  };

  // Add this function to calculate the color based on percentage
  const getActivatedColor = (activated, total) => {
    if (total === 0) return "#9E9E9E"; // Grey if no licenses
    const percentage = (activated / total) * 100;
    if (percentage >= 75) return "#4CAF50"; // Green
    if (percentage >= 50) return "#FFC107"; // Yellow
    return "#F44336"; // Red
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f6fbff",
      fontFamily: "'Poppins', 'Roboto', sans-serif",
      overflowY: "auto"
    }}>
      {loading && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(255,255,255,0.6)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div style={{
            border: "6px solid #eaf4ff",
            borderTop: "6px solid #263D75",
            borderRadius: "50%",
            width: 48,
            height: 48,
            animation: "spin 1s linear infinite"
          }} />
          <style>
            {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
          </style>
        </div>
      )}
      <header
        style={{
          width: "100%",
          background: "linear-gradient(90deg, #263D75 60%, #4F6CB6 100%)",
          color: "#fff",
          padding: "1rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxSizing: "border-box",
          boxShadow: "0 4px 16px 0 rgba(38,61,117,0.18), 0 1.5px 4px 0 rgba(38,61,117,0.10)",
          borderBottomLeftRadius: "18px",
          borderBottomRightRadius: "18px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        {/* Left: Logo/Brand */}
        <div style={{ fontWeight: "bold", fontSize: "1.5rem", letterSpacing: "1px", display: "flex", alignItems: "center" }}>
          <span style={{
            display: "inline-block",
            width: 36,
            height: 36,
            background: "#fff",
            borderRadius: "50%",
            marginRight: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(38,61,117,0.10)"
          }}>
            {/* <span role="img" aria-label="logo" style={{ fontSize: 22, color: "#263D75" }}>🧑‍💻</span> */}
          </span>
          iCodeT
        </div>
        {/* Center: School Name */}
        <div style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          fontWeight: "bold",
          fontSize: "1.2rem",
          letterSpacing: "1px",
          color: "#fff",
          textAlign: "center",
          pointerEvents: isSuperAdmin ? "auto" : "none",
          minWidth: 200,
          maxWidth: "40vw",
          whiteSpace: "nowrap",
          overflow: "visible",
          textOverflow: "ellipsis",
          zIndex: 20
        }}>
          {isSuperAdmin ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Select
                options={schoolOptions.map(schoolName => ({ value: schoolName, label: schoolName }))}
                value={schoolOptions
                  .map(schoolName => ({ value: schoolName, label: schoolName }))
                  .filter(option => pendingSchoolSelection.includes(option.value))}
                onChange={options => {
                  const nextSchools = (options || []).map(option => option.value);
                  handlePendingSchoolChange(nextSchools);
                }}
                isMulti
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder="Select schools"
                aria-label="Select schools"
                menuPortalTarget={document.body}
                schoolOptions={schoolOptions}
                selectedSchools={pendingSchoolSelection}
                onPendingSchoolChange={handlePendingSchoolChange}
                components={{ Option: SchoolOption, MenuList: SchoolMenuList, ValueContainer: SchoolValueContainer, MultiValue: () => null }}
                styles={{
                  container: base => ({ ...base, width: 320, minWidth: 260, maxWidth: "40vw" }),
                  control: (base, state) => ({
                    ...base,
                    height: 44,
                    minHeight: 44,
                    maxHeight: 44,
                    borderRadius: 10,
                    border: "2px solid rgba(255,255,255,0.85)",
                    boxShadow: state.isFocused ? "0 0 0 3px rgba(255,255,255,0.25)" : "0 3px 10px rgba(0,0,0,0.16)",
                    cursor: "pointer"
                  }),
                  valueContainer: base => ({
                    ...base,
                    height: 40,
                    flexWrap: "nowrap",
                    overflow: "hidden"
                  }),
                  placeholder: base => ({ ...base, color: "#68789c" }),
                  menuPortal: base => ({ ...base, zIndex: 99999 }),
                  menu: base => ({ ...base, borderRadius: 10, overflow: "hidden" }),
                  option: (base, state) => ({
                    ...base,
                    color: "#263D75",
                    backgroundColor: state.isFocused ? "#eaf4ff" : "#fff",
                    cursor: "pointer"
                  })
                }}
              />
              <button
                type="button"
                onClick={() => onSchoolChange(pendingSchoolSelection)}
                style={{
                  height: 44,
                  padding: "0 16px",
                  border: "none",
                  borderRadius: 8,
                  background: "#fff",
                  color: "#263D75",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 3px 10px rgba(0,0,0,0.16)"
                }}
              >
                Search
              </button>
            </div>
          ) : (
            user.schoolName
          )}
        </div>
        {/* Right: Logout */}
        <button
          onClick={() => setShowLogoutModal(true)}
          style={{
            background: "linear-gradient(90deg, #fff 60%, #eaf4ff 100%)",
            color: "#263D75",
            border: "none",
            borderRadius: "8px",
            padding: "0.55rem 1.4rem",
            fontWeight: "bold",
            cursor: "pointer",
            fontSize: "1rem",
            boxShadow: "0 2px 8px rgba(38,61,117,0.10)",
            transition: "background 0.2s, box-shadow 0.2s",
            outline: "none",
          }}
        >
          Logout
        </button>
      </header>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.35)",
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div style={{
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(38,61,117,0.18)",
            padding: "2rem 2.5rem",
            minWidth: 320,
            maxWidth: "90vw",
            textAlign: "center",
            fontFamily: "'Poppins', 'Roboto', sans-serif"
          }}>
            <div style={{ fontSize: "1.3rem", fontWeight: "bold", color: "#263D75", marginBottom: "1rem" }}>
              Confirm Logout
            </div>
            <div style={{ color: "#555", marginBottom: "2rem" }}>
              Are you sure you want to logout?
            </div>
            <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
              <button
                onClick={() => {
                  setShowLogoutModal(false);
                  if (pendingLogout) {
                    setPendingLogout(false);
                  }
                }}
                style={{
                  background: "#eaf4ff",
                  color: "#263D75",
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.6rem 1.4rem",
                  fontWeight: "bold",
                  cursor: "pointer",
                  fontSize: "1rem",
                  boxShadow: "0 2px 8px rgba(38,61,117,0.10)",
                  transition: "background 0.2s, box-shadow 0.2s",
                  outline: "none"
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLogoutModal(false);
                  setPendingLogout(false);
                  onLogout();
                }}
                style={{
                  background: "#263D75",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.6rem 1.4rem",
                  fontWeight: "bold",
                  cursor: "pointer",
                  fontSize: "1rem",
                  boxShadow: "0 2px 8px rgba(38,61,117,0.10)",
                  transition: "background 0.2s, box-shadow 0.2s",
                  outline: "none"
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: "2rem" }}>
        {/* Welcome Section with License Cards */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "2rem"
        }}>
          {/* Left: Welcome Text */}
          <div>
            <h1 style={{ color: "#263D75", marginBottom: "0.4rem", margin: 0 }}>
              Welcome, {user.name || user.license}!
            </h1>
            <p style={{
              color: "#4F6CB6",
              fontWeight: 400,
              fontSize: "1.08rem",
              margin: 0,
              lineHeight: 1.5
            }}>
              Here's a quick overview of your school's key stats.
            </p>
          </div>

          {/* Right: License Cards */}
          <div style={{
            display: "flex",
            gap: "0.8rem",
            alignItems: "stretch"
          }}>
            {/* Total Licenses Card */}
            <div style={{
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
              padding: "0.8rem 1rem",
              textAlign: "center",
              minWidth: "120px",
              borderLeft: "4px solid #2196F3",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
              <div style={{
                fontSize: "1.5rem",
                color: "#2196F3",
                fontWeight: "bold",
                marginBottom: "0.2rem"
              }}>
                {stats.totalLicenses || 0}
              </div>
              <div style={{
                color: "#2196F3",
                fontWeight: "600",
                fontSize: "0.8rem"
              }}>
                Total Licenses
              </div>
            </div>

            {/* Total Activated Card */}
            <div style={{
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
              padding: "0.8rem 1rem",
              textAlign: "center",
              minWidth: "120px",
              borderLeft: `4px solid ${getActivatedColor(stats.totalActivated, stats.totalLicenses)}`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
              <div style={{
                fontSize: "1.5rem",
                color: getActivatedColor(stats.totalActivated, stats.totalLicenses),
                fontWeight: "bold",
                marginBottom: "0.2rem"
              }}>
                {stats.totalActivated || 0}
              </div>
              <div style={{
                color: getActivatedColor(stats.totalActivated, stats.totalLicenses),
                fontWeight: "600",
                fontSize: "0.8rem"
              }}>
                Total Activated
              </div>

              {/* Simple Percentage Display */}
              <div style={{
                marginTop: "0.3rem",
                fontSize: "0.7rem",
                color: getActivatedColor(stats.totalActivated, stats.totalLicenses),
                fontWeight: "600",
                opacity: 0.8
              }}>
                {stats.totalLicenses > 0 ?
                  `${Math.round((stats.totalActivated / stats.totalLicenses) * 100)}%`
                  : "0%"
                }
              </div>
            </div>
          </div>
        </div>

        {/* Info Message */}
        {infoMsg && (
          <div style={{
            margin: "0 0 1rem 0",
            color: "#d32f2f",
            background: "#fff3f3",
            border: "1px solid #ffcdd2",
            borderRadius: "6px",
            padding: "0.7rem 1.2rem",
            fontWeight: "bold"
          }}>
            {infoMsg}
          </div>
        )}

        <div
          style={{
            marginTop: "2rem",
            display: "grid",
            gridTemplateColumns: "260px 1fr",
            gap: "2rem",
            alignItems: "stretch",
            minHeight: 360
          }}
        >
          {/* Stats Cards */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              height: "100%",
              justifyContent: "stretch"
            }}
          >
            <div style={{
              ...cardBaseStyle,
              borderLeft: `6px solid ${colorTrainers}`,
              cursor: "pointer"
            }}
              onClick={() => handleCardClick("trainers")}
            >
              <div style={{ fontSize: "2.2rem", color: colorTrainers, fontWeight: "bold" }}>{stats.trainers}</div>
              <div style={{ color: colorTrainers, fontWeight: "bold" }}>Total Trainers</div>
            </div>
            <div style={{
              ...cardBaseStyle,
              borderLeft: `6px solid ${colorStudents}`,
              cursor: "pointer"
            }}
              onClick={() => handleCardClick("students")}
            >
              <div style={{ fontSize: "2.2rem", color: colorStudents, fontWeight: "bold" }}>{stats.students}</div>
              <div style={{ color: colorStudents, fontWeight: "bold" }}>Total Students</div>
            </div>
            <div style={{
              ...cardBaseStyle,
              borderLeft: `6px solid ${colorSystems}`,
              cursor: "pointer"
            }}
              onClick={() => handleCardClick("systems")}
            >
              <div style={{ fontSize: "2.2rem", color: colorSystems, fontWeight: "bold" }}>{stats.systems}</div>
              <div style={{ color: colorSystems, fontWeight: "bold" }}>Total Systems</div>
            </div>
          </div>
          {/* Highcharts Bar Chart */}
          <div
            className="dashboard-chart-pdf"
            style={{
              background: "#fff",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
              display: "block",
              minHeight: 340,
              width: "100%",
              height: "100%",
              padding: 0,
              margin: 0
            }}
          >
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              containerProps={{ style: { width: "100%", height: "100%" } }}
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </div>
        {/* Data Table */}
        <div style={{
          marginTop: "0.5rem",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
          padding: "1.5rem",
          overflowX: "auto"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h2 style={{ color: "#263D75", marginBottom: "1rem", marginTop: "-5px", fontSize: "1.15rem" }}>
              School Users
            </h2>
            <button
              onClick={exportTableAsPDF}
              style={{
                background: "#263D75",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "0.5rem 1.2rem",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "1rem",
                marginBottom: "1rem"
              }}
            >
              Export as PDF
            </button>
          </div>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "'Poppins', 'Roboto', sans-serif",
            fontSize: "1rem"
          }}>
            <thead>
              <tr style={{ background: "#eaf4ff" }}>
                <th style={{ padding: "0.7rem", textAlign: "left", borderRadius: "6px 0 0 6px", cursor: "pointer" }}
                  onClick={() => setSortConfig(sortConfig.key === "name" ? { key: "name", direction: sortConfig.direction === "asc" ? "desc" : "asc" } : { key: "name", direction: "asc" })}>
                  Name {sortConfig.key === "name" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Type</th>
                <th style={{ padding: "0.7rem", textAlign: "left", cursor: "pointer" }}
                  onClick={() => setSortConfig(sortConfig.key === "standard" ? { key: "standard", direction: sortConfig.direction === "asc" ? "desc" : "asc" } : { key: "standard", direction: "asc" })}>
                  Standard {sortConfig.key === "standard" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ padding: "0.7rem", textAlign: "left", cursor: "pointer" }}
                  onClick={() => setSortConfig(sortConfig.key === "volume" ? { key: "volume", direction: sortConfig.direction === "asc" ? "desc" : "asc" } : { key: "volume", direction: "asc" })}>
                  Volume {sortConfig.key === "volume" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>License_key</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Password</th>
                <th style={{ padding: "0.7rem", textAlign: "left", cursor: "pointer" }}
                  onClick={() => setSortConfig(sortConfig.key === "status" ? { key: "status", direction: sortConfig.direction === "asc" ? "desc" : "asc" } : { key: "status", direction: "asc" })}>
                  Status {sortConfig.key === "status" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Last Used on</th>
                <th style={{ padding: "0.7rem", textAlign: "left", borderRadius: "0 6px 6px 0" }}>DOB</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", color: "#aaa", padding: "1.5rem" }}>
                    No data found for this school.
                  </td>
                </tr>
              )}
              {sortedRows.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "0.6rem" }}>{row.name}</td>
                  <td style={{ padding: "0.6rem", textTransform: "capitalize" }}>{row.type}</td>
                  <td style={{ padding: "0.6rem" }}>
                    {row.standard !== undefined && row.standard !== null && String(row.standard).trim() !== "" ? String(row.standard) : "-"}
                  </td>
                  <td style={{ padding: "0.6rem" }}>{row.volume}</td>
                  <td style={{ padding: "0.6rem" }}>{row.license}</td>
                  <td style={{ padding: "0.6rem" }}>{row.password}</td>
                  <td style={{ padding: "0.6rem" }}>
                    {isSuperAdmin ? (
                      <select
                        value={
                          row.status && row.status.trim()
                            ? (row.status.trim().toLowerCase() === "inactive"
                              ? "Deactivated"
                              : row.status.trim().toLowerCase() === "pending" || row.status.trim() === ""
                                ? "Not Activated"
                                : row.status)
                            : "Not Activated"
                        }
                        onChange={e => {
                          let val = e.target.value;
                          if (val === "Deactivated") val = "Inactive";
                          if (val === "Not Activated") val = "Pending";
                          handleStatusChange(idx, val);
                        }}
                        style={{
                          fontFamily: "'Poppins', 'Roboto', sans-serif",
                          fontSize: "1rem",
                          padding: "0.2rem 0.5rem",
                          borderRadius: "4px",
                          border: "1px solid #e0e0e0",
                          background: "#f6fbff"
                        }}
                      >
                        <option value="Active">Active</option>
                        <option value="Deactivated">Deactivated</option>
                        <option value="Not Activated">Not Activated</option>
                      </select>
                    ) : (
                      row.status && row.status.trim()
                        ? (row.status.trim().toLowerCase() === "inactive"
                          ? "Deactivated"
                          : row.status.trim().toLowerCase() === "pending" || row.status.trim() === ""
                            ? "Not Activated"
                            : row.status)
                        : "Not Activated"
                    )}
                  </td>
                  <td style={{ padding: "0.6rem" }}>{row.last_used_on}</td>
                  <td style={{ padding: "0.6rem" }}>{row.dob}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

